import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CopyToClipboard from 'react-copy-to-clipboard'

import {
  Autocomplete,
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Theme,
  Tooltip,
  Typography
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { formatDateAsString } from '../../../util/formatDateAsString'
import { formatTimeAsString } from '../../../util/formatTimeAsString'

import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'

import { EUserRole, NetworkType, UserLogin, UserSettings } from '@professocialApi/professocial-core-frontend-api'

import {
  createUserAccountInitiated,
  forceDisconnectInitiated,
  getPageIdentifiersForUserInitiated,
  getTokenForPageInitiated,
  updateUserAccountInitiated,
} from '../../../store/accounts/accounts.actions'
import { SaveSuccess } from '../../../store/accounts/accounts.reducer'
import { getPageToken, onSuccess } from '../../../store/accounts/accounts.selector'
import { getLanguages, namedRequestData, namedRequestInProgress } from '../../../store/common/common.selectors'
import { useHistory, useParams } from 'react-router'
import { ResultPageWebModel, UserHasPage } from '@professocialApi/professocial-core-frontend-api/models'
import { EventType, Message, Severity } from '@professocial/professocialTypes/types/messageTypes'
import { setMessage } from '../../../store/common/common.actions'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { ActionCall, Languages } from '@professocial/professocialTypes/types'
import { RootState } from '../../../store/accountRoot.reducer'
import FacebookFanPage from './FanPage'
import { ForceDisconnectRequest } from '@professocialApi/professocial-core-frontend-api/apis/SmPublishConnectApi'
import addTimezone from '../../../util/addTimezone'
import { GetTokenForPageRequest } from '@professocialApi/professocial-core-frontend-api/apis/TmpAdminApi'

export interface DropDownElement {
  key: string;
  value: string;
}

interface IAccountEditProps {
  data?: ResultPageWebModel;
  user?: UserSettings;
  subsidiaries?: any;
  mandators?: any;
  onEdit?: boolean;
  role?: string;
  backToOverview?: () => void;
  createNewUser?: (data: any) => void;
  changeUserData?: (data: any) => void;
  handleMessage?: (data: any) => void;
  resetFailedLoginCount?: (data: any) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3, 2),
  },
  textField: {
    '& .MuiFormHelperText-root': {
      lineHeight: '0.95 !important',
    },
  },
  hiddenTextField: {
    width: '0',
  },
  formDivide: {
    Width: '100%',
    borderBottom: '1px solid #808080',
    lineHeight: '0.1em',
    margin: '10px 0 20px',
    textAlign: 'justify',
  },
  formDivide_text: {
    paddingRight: '10px',
  },
  formDivide_box: {
    padding: theme.spacing(0),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    textAlign: 'center',
  },
  fbPage: {
    padding: theme.spacing(2),
    width: '100%',
  },
  fbPagePart: {
    padding: theme.spacing(4),
    paddingTop: 0,
  },
  fbPageInfo: {
    flexBasis: '15%',
  },
  fbPageDisconnect: {
    padding: theme.spacing(2),
  },
  fbAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  fbText: {
    paddingLeft: theme.spacing(1),
  },
  inputButton: {
    minWidth: '0',
    padding: '0',
    width: '0',
    position: 'relative',
    left: '-10%',
    marginTop: theme.spacing(2.75),
    '&:hover': {
      backgroundColor: '#fff',
      color: theme.palette.primary.dark,
    },
  },
}))

export type InputAttr = {
  inputComponent: string
  id: 'username' | 'lastLogin' | 'lastAppLogin' | 'createdAt' | 'failedLoginCount' | 'firstName' | 'lastName' | 'email' | 'password' | 'passwordAgain' | 'agencyNumber' | 'role' | 'language' | 'mandator' | 'subsidiary' | 'finishedQuiz' | 'finishedWizard' | 'commitmentsConfirmed' | 'nonValidate'
  label: string
  type?: string
  active?: boolean
  disabled?: boolean
  options?: any
  visibility?: boolean
  required?: boolean
  handleChange?: any
  autoComplete?: string
  additionalButton?: boolean
  additionalButtonIcon?: any
  additionalButtonText?: string
  additionalButtonOnClick?: () => void
}

type INewTopicForm = {
  username: string
  lastLogin?: string
  lastAppLogin?: string
  createdAt?: string
  failedLoginCount?: number
  firstName?: string
  lastName?: string
  password?: string
  passwordAgain?: string
  agencyNumber?: string
  role?: DropDownElement
  language?: DropDownElement
  mandator?: DropDownElement
  subsidiary?: DropDownElement
  email?: string
  finishedQuiz: boolean
  finishedWizard: boolean
  commitmentsConfirmed: boolean
  nonValidate: boolean
}

const AccountEdit: React.FC<IAccountEditProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()

  const storedLanguages: Languages[] | undefined = useSelector(getLanguages)
  const xsrfToken = sessionStorage.getItem('XSRF-TOKEN')

  const dispatchGetPageIdentifiersForUser = useCallback(() => dispatch(getPageIdentifiersForUserInitiated()), [dispatch])
  const dispatchSetMessage = useCallback((message: Message) => dispatch(setMessage(message)), [dispatch])
  const dispatchForceDisconnect = useCallback((param: ForceDisconnectRequest) => dispatch(forceDisconnectInitiated(param)), [dispatch])
  const dispatchGetPageToken = useCallback((param: GetTokenForPageRequest) => dispatch(getTokenForPageInitiated(param)), [dispatch])

  const saveSuccess = useSelector(onSuccess)
  const pageToken = useSelector(getPageToken)
  const actionOnUserCreate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, createUserAccountInitiated.type))
  const actionOnUserUpdate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, updateUserAccountInitiated.type))

  const creatingUser: boolean = useSelector((state: RootState) => namedRequestInProgress(state, createUserAccountInitiated.type))
  const updatingUser: boolean = useSelector((state: RootState) => namedRequestInProgress(state, updateUserAccountInitiated.type))

  const [data, setData] = useState<UserLogin | undefined>()
  const [subsidiaries, setSubsidiaries] = useState<DropDownElement[]>()
  const [mandators, setMandators] = useState<DropDownElement[]>()
  const [usernameLabel, setUsernameLabel] = useState<string>(t('accountsCommon:copyUsername'))

  const onEdit = props.onEdit
  const roleOptions: DropDownElement[] = [
    {
      value: t('accountsCommon:user'),
      key: EUserRole.User,
    },
    {
      value: t('accountsCommon:detainee'),
      key: EUserRole.NotConfirmed,
    },
  ]
  props.role === 'ADMIN' && roleOptions.push(
    {
      value: t('accountsCommon:admin'),
      key: EUserRole.Admin,
    },
    {
      value: t('accountsCommon:editor'),
      key: EUserRole.Editor,
    },
    {
      value: t('accountsCommon:subsidiaryAdmin'),
      key: EUserRole.Subsidiary,
    },
  )

  const languages: DropDownElement[] = storedLanguages
    ? storedLanguages.map((lang: Languages) => {
      return { key: lang, value: t(`coreCommon:${lang.valueOf()}`) }
    }) as DropDownElement[]
    : Object.keys(Languages).map((lang: string) => {
      return { key: lang, value: t(`coreCommon:${lang.valueOf()}`) }
    }) as DropDownElement[]

  const statusSwitches = [
    {
      id: 'finishedQuiz',
      label: t('accountsCommon:finishedQuiz'),
    },
    {
      id: 'finishedWizard',
      label: t('accountsCommon:finishedWizard'),
    },
    {
      id: 'commitmentsConfirmed',
      label: t('accountsCommon:commitmentsConfirmed'),
    },
    {
      id: 'nonValidate',
      label: t('accountsCommon:nonValidate'),
    },
  ]

  const getDateString = (date: number) => {
    const timezoneDate = addTimezone(new Date(date * 1000))
    return `${formatDateAsString(timezoneDate.getTime())} ${formatTimeAsString(timezoneDate.getTime())}`
  }

  const defaultValues: INewTopicForm = {
    username: (data && data.username) || '',
    lastLogin: data?.lastLogin ? getDateString(data.lastLogin) : undefined,
    lastAppLogin: data?.lastAppLogin ? getDateString(data.lastAppLogin) : undefined,
    createdAt: data?.createdAt ? getDateString(data.createdAt) : undefined,
    failedLoginCount: data && data.failedLoginCount,
    firstName: data && data.firstName,
    lastName: data && data.lastName,
    password: data && data.password,
    passwordAgain: '',
    mandator: data && data.mandator && { key: data.mandator.id, value: data.mandator.label } as DropDownElement,
    subsidiary: data && data.subsidiary && { key: data.subsidiary.id, value: data.subsidiary.label } as DropDownElement,
    email: data && data.email,
    agencyNumber: data && data?.agency?.agencyNumber,
    role: (data && data.role && roleOptions.find(item => item.key === data.role) as DropDownElement) || roleOptions[0],
    language: data && languages.find(item => item.key === data.localeCode) as DropDownElement,
    finishedQuiz: (data && data.finishedQuiz) || false,
    finishedWizard: (data && data.finishedWizard) || false,
    commitmentsConfirmed: (data && data.commitmentsConfirmed) || false,
    nonValidate: (data && data.nonValidate) || false,
  }

  const validationSchema = Yup.object<Record<keyof INewTopicForm, Yup.AnySchema>>().shape({
    username: Yup.string().required(`${t('accountsCommon:missingInputError')}`),
    lastLogin: Yup.string(),
    lastAppLogin: Yup.string(), // `${}`
    createdAt: Yup.string(),
    failedLoginCount: Yup.string(),
    firstName: Yup.string().required(`${t('accountsCommon:missingInputError')}`),
    lastName: Yup.string().required(`${t('accountsCommon:missingInputError')}`),
    password: onEdit ? Yup.string().matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$%\-@_]).{6,20}$/, {
      message: `${t('coreSettings:descPass')}`,
      excludeEmptyString: true
    }) : Yup.string()
      .required(`${t('accountsCommon:passwordsError')}`)
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$%\-@_]).{6,20}$/, `${t('coreSettings:descPass')}`),
    passwordAgain: Yup.string().when('password', {
      is: (password: string) => password?.length > 0,
      then: Yup.string()
        .required(`${t('accountsCommon:passwordsError')}`)
        .test('passwords-match', `${t('accountsCommon:passwordsError')}`, function (value) {
          return this.parent.password === value
        }),
    }),
    agencyNumber: Yup.string(),
    mandator: (props.role === 'ADMIN' && mandators?.length !== 0) ? Yup.object().shape({
      key: Yup.string().required(`${t('accountsCommon:missingInputError')}`),
      value: Yup.string().required(`${t('accountsCommon:missingInputError')}`),
    }) : Yup.object().shape({
      key: Yup.string(),
      value: Yup.string(),
    }),
    email: Yup.string().required(`${t('accountsCommon:missingInputError')}`).email(),
  })

  const {
    register,
    reset,
    getValues,
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<INewTopicForm>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const userNameValue = getValues('username')

  const configMandators = () => {
    const array: DropDownElement[] = []
    props.mandators && props.mandators.map((elem: any) => {
      if (elem && elem.id && elem.label) {
        array.push({
          key: elem.id,
          value: elem.label,
        })
      }
    })
    return array
  }

  const configSubsidiaries = () => {
    const array: DropDownElement[] = []
    props.subsidiaries && props.subsidiaries.map((elem: any) => {
      // if (data && elem.mandatorId === data.mandator?.id) {
      array.push({
        key: elem.id,
        value: elem.label,
      })
      // }
    })
    return array
  }

  const disconnectPage = (userHasPage: UserHasPage) => {
    const params: ForceDisconnectRequest = {
      userId: id,
      userHasPage: userHasPage
    }

    dispatchForceDisconnect(params)
  }


  useEffect(() => {
    dispatchGetPageIdentifiersForUser()
  }, [data])

  useEffect(() => {
    reset(defaultValues)
  }, [reset, data])

  useEffect(() => {
    if (saveSuccess === SaveSuccess.unmountFacebookPageByUser) {
      dispatchSetMessage({
        message: t('accountsCommon:disconnectedFacebook'),
        severity: Severity.success,
        eventType: EventType.create,
      })
      data && setData({ ...data })
    }
  }, [saveSuccess])

  useEffect(() => {
    if (props.subsidiaries) {
      setSubsidiaries(configSubsidiaries())
    }
    if (props.mandators) {
      setMandators(configMandators())
    }
  }, [props.subsidiaries, props.mandators])

  useEffect(() => {
    setData(data)
  }, [data])

  useEffect(() => {
    if (id) {
      props.data && setData(props.data.entries?.find((element: UserLogin) => element.id === id))
    }
  }, [id, props.data])

  const resetFailedLoginCount = () => {
    const requestData = {
      ...data,
      failedLoginCount: -1,
    }
    props.resetFailedLoginCount && props.resetFailedLoginCount(requestData)
  }

  const usernameCopied = () => {
    setUsernameLabel(t('accountsCommon:usernameCopied'))
    setTimeout(() => {
      setUsernameLabel(t('accountsCommon:copyUsername'))
    }, 2000)
  }

  const showToken = () => {
    const userHasPage = data?.userHasPages?.find(page => page.networkType === NetworkType.Facebook)

    userHasPage && dispatchGetPageToken({
      networkPageId: userHasPage.networkPageId,
      networkType: userHasPage.networkType,
      networkUserId: userHasPage.networkUserId
    })
  }


  useEffect(() => {
    if (pageToken) {
      props.handleMessage && props.handleMessage({
        autoClose: false,
        confirmLabel: t('accountsCommon:validateToken'),
        cancelLabel: t('accountsCommon:cancel'),
        open: true,
        title: 'Token',
        message: pageToken,
        severity: 'success',
        requestActions: true,
        handleClose: () => props.handleMessage && props.handleMessage(null),
        handleCancel: () => props.handleMessage && props.handleMessage(null),
        handleConfirm: () => {
          props.handleMessage && props.handleMessage(null)
          validateToken(pageToken)
        },
      })
    }
  }, [pageToken])


  const validateToken = (token: string) => {
    window.open(`https://developers.facebook.com/tools/debug/accesstoken/?access_token=${token}`, '_blank')
  }

  // functions for when the form is submitted
  const changeUserData: SubmitHandler<INewTopicForm> = entryData => {
    if (data) {
      const changedData = {
        ...data,
        ...entryData,
        lastLogin: data.lastLogin,
        lastAppLogin: data.lastAppLogin,
        createdAt: data.createdAt,
        role: entryData.role?.key,
        password: entryData?.password?.length === 0 ? undefined : entryData.password,
        agency: entryData.agencyNumber,
        localeCode: entryData.language?.key,
        subsidiary: entryData.subsidiary ? {
          id: entryData.subsidiary?.key,
          label: entryData.subsidiary?.value
        } : undefined,
        mandator: props.role === 'ADMIN' && entryData.mandator ? { id: entryData.mandator.key } : props.user?.mandator,
      } as UserLogin

      props.changeUserData && props.changeUserData(changedData)
    }
  }

  const createNewUser: SubmitHandler<INewTopicForm> = entryData => {
    if (entryData && entryData.role) {
      const newData: UserLogin = {
        role: entryData.role.key as EUserRole,
        agency: props.user?.agency,
        localeCode: entryData.language?.key,
        finishedQuiz: entryData.finishedQuiz,
        finishedWizard: entryData.finishedWizard,
        commitmentsConfirmed: entryData.commitmentsConfirmed,
        enableBanner: false,
        nonValidate: entryData.nonValidate,
        username: entryData.username,
        firstName: entryData.firstName,
        lastName: entryData.lastName,
        email: entryData.email,
        password: entryData.password,
        mandator: props.role === 'ADMIN' && entryData.mandator ? { id: entryData.mandator.key } : props.user?.mandator,
        subsidiary: entryData.subsidiary ? {
          id: entryData.subsidiary?.key,
          label: entryData.subsidiary?.value
        } : undefined,
      }

      props.createNewUser && props.createNewUser(newData)
    }
  }

  const inputComponents: InputAttr[] = [
    {
      inputComponent: 'text',
      id: 'username',
      label: `${t('accountsCommon:username')}`,
      disabled: onEdit,
      visibility: true,
      required: true,
      autoComplete: 'off',
      additionalButton: props.onEdit,
      additionalButtonIcon: <FileCopyOutlinedIcon/>,
      additionalButtonText: usernameLabel,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'lastLogin',
      label: `${t('accountsCommon:lastLogin')}`,
      disabled: true,
      required: false,
      visibility: onEdit,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'lastAppLogin',
      label: `${t('accountsCommon:lastAppLogin')}`,
      disabled: true,
      autoComplete: 'off',
      required: false,
      visibility: onEdit,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'createdAt',
      label: `${t('accountsCommon:createdAt')}`,
      disabled: true,
      required: false,
      visibility: onEdit,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'failedLoginCount',
      label: `${t('accountsCommon:failedLoginCount')}`,
      disabled: true,
      required: false,
      visibility: onEdit,
      additionalButton: props.onEdit,
      additionalButtonIcon: <HighlightOffIcon/>,
      additionalButtonText: t('accountsCommon:resetFailedLogin'),
      additionalButtonOnClick: () => resetFailedLoginCount(),
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'firstName',
      label: `${t('accountsCommon:firstName')}`,
      disabled: false,
      autoComplete: 'off',
      required: true,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'lastName',
      label: `${t('accountsCommon:lastName')}`,
      disabled: false,
      autoComplete: 'off',
      required: true,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'email',
      type: 'email',
      label: `${t('accountsCommon:email')}`,
      disabled: false,
      autoComplete: 'off',
      required: true,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'password',
      type: 'password',
      label: `${t('accountsCommon:password')}`,
      disabled: false,
      autoComplete: 'off',
      required: !props.onEdit,
      visibility: props.user?.role !== 'EDITOR',
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'passwordAgain',
      type: 'password',
      label: `${t('accountsCommon:passwordAgain')}`,
      disabled: false,
      autoComplete: 'off',
      required: !props.onEdit,
      visibility: props.user?.role !== 'EDITOR',
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'agencyNumber',
      label: `${t('accountsCommon:agencyNumber')}`,
      disabled: false,
      required: false,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'dropDown',
      id: 'role',
      label: `${t('accountsCommon:role')}`,
      options: roleOptions,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'dropDown',
      id: 'language',
      label: `${t('accountsCommon:language')}`,
      options: languages,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'dropDown',
      id: 'mandator',
      label: `${t('accountsCommon:mandator')}`,
      options: mandators,
      required: true,
      visibility: true,
      active: (props.role === 'ADMIN' && mandators && mandators.length !== 0),
    },
    {
      inputComponent: 'dropDown',
      id: 'subsidiary',
      label: `${t('accountsCommon:subsidiary')}`,
      options: subsidiaries,
      visibility: true,
      active: ((props.role === 'ADMIN' || props.role === 'EDITOR') && subsidiaries && subsidiaries.length !== 0),
    },
  ]

  const aboSettings = data?.id === id && data?.userSettings

  const licensePermissions = props.user?.mandator?.licensePermissions
  const isNetworkLicensed = (networkType: NetworkType | undefined): boolean => {
    if (!networkType || !licensePermissions) return true
    switch (networkType) {
    case NetworkType.Facebook: return licensePermissions.canUseFacebook
    case NetworkType.Instagram: return licensePermissions.canUseInstagram
    case NetworkType.GoogleMyBusiness: return licensePermissions.canUseGoogleMyBusiness
    case NetworkType.LinkedIn: return licensePermissions.canUseLinkedIn
    default: return true
    }
  }
  const visibleAboSettings = Array.isArray(aboSettings)
    ? aboSettings.filter(settings => isNetworkLicensed(settings.aboSettings?.[0]?.pageIdentifier?.networkType))
    : aboSettings

  return (
    <Paper sx={{ py: 4, px: 3, mb: 7 }}>
      <form autoComplete="off">
        <Box sx={{ mb: 2 }}>
          <ButtonComponent text={t('accountsCommon:backToOverview')} variant="contained"
            onClick={() => history.goBack()} disableElevation color="primary"/>
        </Box>
        <br/>
        <Grid container item xs={12} spacing={5}>
          {inputComponents && inputComponents.length !== 0 && inputComponents.map((row, index: number) => (
            <React.Fragment key={index}>
              {row.active && row.visibility && (
                <Grid item xs={12} sm={6} md={4} lg={3}
                  sx={{ marginBottom: errors[row.id]?.message && '-24px' }}>
                  {row.inputComponent === 'text'
                    ? (
                      <TextFieldComponent
                        {...register(row.id)}
                        id={row.id}
                        label={row.label}
                        type={row.type}
                        size="small"
                        autoComplete={row.autoComplete}
                        disabled={row.disabled}
                        required={row.required}
                        error={!!errors[row.id]}
                        helperText={errors[row.id]?.message}
                        InputProps={props.onEdit && (row.id === 'username' || row.additionalButton) ? {
                          endAdornment: (
                            <>
                              {row.id === 'username' && userNameValue &&
                                <InputAdornment position="end">
                                  <CopyToClipboard
                                    text={userNameValue}
                                    onCopy={() => usernameCopied()}
                                  >
                                    <Tooltip title={`${row.additionalButtonText}`}>
                                      <IconButton sx={{ mr: -1 }} color="info"
                                        size="small">
                                        {row.additionalButtonIcon}
                                      </IconButton>
                                    </Tooltip>
                                  </CopyToClipboard>
                                </InputAdornment>}
                              {row.id !== 'username' && row.additionalButton && (
                                <InputAdornment position="end">
                                  <Tooltip title={`${row.additionalButtonText}`}>
                                    <IconButton sx={{ mr: -1 }} color="info"
                                      size="small"
                                      onClick={() => row.additionalButtonOnClick && row.additionalButtonOnClick()}>
                                      {row.additionalButtonIcon}
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              )}
                            </>),
                          autoComplete: 'new-password',
                        } : { autoComplete: 'new-password' }}
                        fullWidth
                      />
                    )
                    : (
                      <>
                        {row && row.options &&
                          <Controller
                            control={control}
                            name={row.id}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                id={row.id}
                                value={value as DropDownElement || null}
                                options={row.options}
                                onChange={(event, item) => {
                                  onChange(item)
                                }}
                                getOptionLabel={(option: DropDownElement) => t(`topicsCommon:${option.value}`)}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                fullWidth
                                size="small"
                                disableClearable
                                renderInput={(params) => (
                                  <TextFieldComponent
                                    {...params}
                                    label={row.label}
                                    required={row.required}
                                    name={row.id}
                                  />
                                )}
                              />
                            )}
                          />}
                      </>
                    )}
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
        {data && data.role === 'USER' && (
          <>
            <Box className={classes.formDivide_box} display="flex">
              <div style={{ width: '100%' }}>
                <Box display="flex" flexWrap="wrap">
                  <Box>
                    <Typography
                      className={classes.formDivide_text}
                    >{t('accountsCommon:statusHeadline')}
                    </Typography>
                  </Box>
                  <Box flexGrow={1}>
                    <Typography
                      className={classes.formDivide}
                    >&nbsp;
                    </Typography>
                  </Box>
                </Box>
              </div>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {statusSwitches.map((row: any, index: number) => (
                <Box key={index}>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name={row.id}
                        render={({ field }) => (
                          <Switch
                            id={row.id}
                            {...field}
                            checked={field.value || false}
                            color="primary"
                          />
                        )}
                      />
                    }
                    label={row.label}
                    labelPlacement="start"
                  />
                </Box>
              ))}
            </Stack>

            {props.user && props.user.role === 'ADMIN' &&
              <>
                <Box className={classes.formDivide_box} display="flex">
                  <div style={{ width: '100%' }}>
                    <Box display="flex" flexWrap="wrap">
                      <Box>
                        <Typography
                          className={classes.formDivide_text}
                        >{t('accountsCommon:accessToken')}
                        </Typography>
                      </Box>
                      <Box flexGrow={1}>
                        <Typography
                          className={classes.formDivide}
                        >&nbsp;
                        </Typography>
                      </Box>
                    </Box>
                  </div>
                </Box>
                <Box display="flex" className={classes.fbPage}>
                  {data?.userHasPages?.length
                    ?(
                      <ButtonComponent
                        variant="contained"
                        color="primary"
                        onClick={showToken}
                        text={t('accountsCommon:showToken')}
                      />
                    ) : (
                      <Typography>
                        Kein Token hinterlegt!
                      </Typography>
                    )
                  }
                </Box>
              </>}
            {!!visibleAboSettings && !!visibleAboSettings.length
              ? (
                <Box>
                  <Box className={classes.formDivide_box} display="flex">
                    <Box style={{ width: '100%' }}>
                      <Box display="flex" flexWrap="wrap">
                        <Box>
                          <Typography className={classes.formDivide_text}>
                            {t('accountsCommon:aboStatus')}
                          </Typography>
                        </Box>
                        <Box flexGrow={1}>
                          <Typography className={classes.formDivide}>&nbsp;</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Grid container item xs={12}>
                    {[...visibleAboSettings].reverse().map((settings, index) =>
                      <Grid key={index} item xs={12} md={6}>
                        {/* TODO Open when BE added name and fanCount */}
                        <FacebookFanPage
                          settings={settings}
                          userHasPages={data.userHasPages}
                          disconnectPage={disconnectPage}
                        />
                      </Grid>)}
                  </Grid>
                </Box>
              )
              : null}
          </>
        )}
        <Box sx={{ mt: 4 }}>
          <PreloaderButton
            position='right'
            loading={creatingUser || updatingUser}
            onClick={onEdit ? handleSubmit(changeUserData) : handleSubmit(createNewUser)}
            disableButton={
              !isDirty ||
              (actionOnUserCreate?.status === 'pending' && actionOnUserCreate?.inProgress) ||
              (actionOnUserUpdate?.status === 'pending' && actionOnUserUpdate?.inProgress)
            }
            text={onEdit ? t('accountsCommon:saveChange') : t('accountsCommon:createAccount')}
          />
        </Box>
      </form>
    </Paper>
  )
}

export default AccountEdit
